<template>
  <div class="matrix-request-detail">
    <lila-message :alert-data="alert" />

    <h2 class="mr-title">
      Pedido de apoio
    </h2>
    <p class="mr-subtitle">
      O que a equipe assistencial pediu à equipe matricial sobre este paciente.
    </p>

    <p
      v-if="patientRequests.length === 0"
      class="mr-empty"
    >
      Nenhum pedido de apoio para este paciente.
    </p>

    <ul
      v-else
      class="mr-list"
    >
      <li
        v-for="item in patientRequests"
        :key="item.id"
        class="mr-item"
      >
        <div class="mr-item-head">
          <span class="mr-item-reason">{{ reasonLabel(item.reason) }}</span>
          <span
            class="mr-status"
            :class="statusClass(item.status)"
          >{{ item.status }}</span>
        </div>
        <p class="mr-item-meta">
          Recebido em {{ formatDate(item.createdAt) }}
        </p>

        <dl class="mr-fields">
          <div
            v-if="item.requesterName"
            class="mr-field"
          >
            <dt>Solicitado por</dt>
            <dd>{{ item.requesterName }} · equipe assistencial</dd>
          </div>
          <div class="mr-field">
            <dt>Modalidade</dt>
            <dd>
              {{ modality(item.modality).name }}
              <span class="mr-muted">({{ modality(item.modality).description }})</span>
            </dd>
          </div>
          <div class="mr-field">
            <dt>Pergunta clínica</dt>
            <dd class="mr-question">
              {{ item.question }}
            </dd>
          </div>
        </dl>

        <!-- Recebido: a equipe matricial aceita o pedido -->
        <div
          v-if="item.status === status.received"
          class="mr-actions"
        >
          <lila-button
            type="button"
            title="Aceitar pedido"
            @custom-click-event="accept(item)"
          />
        </div>

        <template v-else>
          <p
            v-if="item.acceptedAt"
            class="mr-muted mr-small mr-accepted"
          >
            Aceito em {{ formatDate(item.acceptedAt) }}{{ item.acceptedBy ? ` por ${item.acceptedBy}` : "" }}
          </p>

          <!-- Respondido: parecer registrado -->
          <div
            v-if="item.response"
            class="mr-response"
          >
            <p class="mr-response-title">
              Parecer da equipe matricial
            </p>
            <p class="mr-question">
              {{ item.response.text }}
            </p>
            <p class="mr-muted mr-small">
              {{ item.response.author ? `${item.response.author} · ` : "" }}{{ formatDate(item.response.createdAt) }}
            </p>
            <p
              v-if="item.reassessDueDate"
              class="mr-reassess-note"
            >
              Reavaliação em {{ formatDay(item.reassessDueDate) }}: ação criada para
              {{ item.requesterName || "a equipe assistencial" }}.
            </p>
          </div>

          <!-- Tela 3: discussão do caso entre as equipes -->
          <lila-case-discussion
            :request="item"
            team="matricial"
            @updated="reload"
          />

          <!-- Em discussão: a equipe matricial registra o parecer -->
          <div
            v-if="item.status === status.discussing"
            class="mr-response-form"
          >
            <lila-textarea
              v-model="drafts[item.id]"
              label="Parecer da equipe matricial"
              placeholder="Escreva a orientação para a equipe assistencial."
              :validation="{ $error: showErrorFor === item.id && !(drafts[item.id] || '').trim(), required: true }"
            />

            <!-- Reavaliação: vira uma ação para quem pediu o apoio -->
            <div class="mr-reassess">
              <label class="mr-reassess-toggle">
                <input
                  v-model="reassess[item.id].enabled"
                  type="checkbox"
                >
                Reavaliar em
              </label>
              <input
                v-model.number="reassess[item.id].days"
                type="number"
                min="1"
                max="90"
                class="mr-reassess-days"
                :disabled="!reassess[item.id].enabled"
                aria-label="Número de dias para reavaliar"
              >
              <span>dias</span>
            </div>
            <p
              v-if="reassess[item.id].enabled"
              class="mr-muted mr-small mr-reassess-hint"
            >
              Cria uma ação na aba Ações para {{ item.requesterName || "quem pediu o apoio" }},
              com prazo em {{ formatDay(dueDate(reassess[item.id].days)) }}.
            </p>

            <div class="mr-actions">
              <lila-button
                type="button"
                title="Enviar parecer"
                @custom-click-event="answer(item)"
              />
            </div>
          </div>
        </template>
      </li>
    </ul>
  </div>
</template>

<script>
// ===== POC Cuidados Paliativos (PNCP) =====
// Aba do paciente para a equipe matricial (role 2) ler o pedido de apoio,
// aceitá-lo e responder com um parecer. Dados da API (/api/palliative/matrix-requests).
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import Textarea from "@components/shared/fields/Textarea.vue";
import MatrixCaseDiscussion from "@components/Chat/MatrixCaseDiscussion.vue";
import {
  acceptMatrixRequest,
  listPatientMatrixRequests,
  respondMatrixRequest,
  statusClass,
  MODALITY_OPTIONS,
  REASON_OPTIONS,
  REQUEST_STATUS
} from "@/services/palliative/matrixSupportApi";
import { showApiError } from "@/services/palliative/http";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

export default {
  components: {
    "lila-button": Button,
    "lila-message": Message,
    "lila-textarea": Textarea,
    "lila-case-discussion": MatrixCaseDiscussion
  },
  // Novos pedidos e mensagens chegam ao vivo da aba da equipe assistencial.
  // O rascunho do parecer não é perdido: fica em "drafts", fora dos pedidos.
  mixins: [pocLiveSync("reload")],
  data() {
    return {
      requests: [],
      status: REQUEST_STATUS,
      drafts: {},
      // Por pedido: { enabled, days } do "Reavaliar em x dias".
      reassess: {},
      showErrorFor: null,
      busy: false,
      alert: {
        message: "",
        type: "",
        show: false
      }
    };
  },
  computed: {
    patientRequests() {
      return this.requests;
    }
  },
  created() {
    this.reload();
  },
  methods: {
    statusClass,
    async reload() {
      try {
        this.requests = await listPatientMatrixRequests(this.$route.params.id);
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar os pedidos.", error);
        return;
      }
      this.requests.forEach((r) => {
        if (!this.reassess[r.id]) {
          this.reassess[r.id] = { enabled: false, days: 7 };
        }
      });
    },
    dueDate(days) {
      return this.$moment().add(Number(days) || 0, "days").format("YYYY-MM-DD");
    },
    formatDay(date) {
      return this.$moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
    },
    reasonLabel(id) {
      const option = REASON_OPTIONS.find((o) => o._id === id);
      return option ? option.name : id;
    },
    modality(id) {
      return (
        MODALITY_OPTIONS.find((o) => o._id === id) || { name: id, description: "" }
      );
    },
    formatDate(iso) {
      return this.$moment(iso).format("DD/MM/YYYY [às] HH:mm");
    },
    async accept(item) {
      if (this.busy) return;
      this.busy = true;
      try {
        await acceptMatrixRequest(item.id);
      } catch (error) {
        showApiError(this, this.alert, error);
      } finally {
        this.busy = false;
      }
      await this.reload();
    },
    async answer(item) {
      const text = (this.drafts[item.id] || "").trim();
      if (!text) {
        this.showErrorFor = item.id;
        return;
      }

      const reassess = this.reassess[item.id];
      const days = Number(reassess.days);
      if (reassess.enabled && !(days >= 1)) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          "Informe em quantos dias a equipe deve reavaliar o paciente."
        );
        return;
      }
      if (this.busy) return;
      this.busy = true;
      try {
        // Com reavaliação, a API cria a ação para quem pediu o apoio.
        await respondMatrixRequest(item.id, text, reassess.enabled ? days : null);
      } catch (error) {
        showApiError(this, this.alert, error);
        return;
      } finally {
        this.busy = false;
      }

      this.drafts = { ...this.drafts, [item.id]: "" };
      this.showErrorFor = null;
      await this.reload();

      this.$Utilities.controlAlert(
        null,
        this.alert,
        "success",
        reassess.enabled
          ? "Parecer enviado e ação de reavaliação criada para a equipe assistencial."
          : "Parecer enviado à equipe assistencial."
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./matrixStatus.scss";

$lila-primary: #4f3f82;

.matrix-request-detail {
  width: 100%;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
}

.mr-title {
  color: $lila-primary;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 6px;
}

.mr-subtitle {
  margin-bottom: 20px;
  font-size: 14px;
  color: #6b6b7b;
}

.mr-empty {
  font-size: 14px;
  color: #6b6b7b;
}

.mr-list {
  max-width: 560px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mr-item {
  margin-bottom: 12px;
  padding: 16px 20px;
  border-radius: 10px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

.mr-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mr-item-reason {
  font-size: 15px;
  font-weight: 700;
  color: #2b2b3a;
}

.mr-item-meta {
  margin: 4px 0 16px;
  font-size: 12px;
  color: #6b6b7b;
}

.mr-fields {
  margin: 0;
}

.mr-field {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  dt {
    margin-bottom: 2px;
    font-size: 13px;
    font-weight: 700;
    color: #484848;
  }

  dd {
    margin: 0;
    font-size: 14px;
    color: #2b2b3a;
  }
}

.mr-muted {
  color: #6b6b7b;
}

.mr-small {
  font-size: 12px;
}

.mr-question {
  font-size: 14px;
  color: #2b2b3a;
  white-space: pre-line;
}

.mr-actions {
  margin-top: 16px;

  :deep(.field) {
    margin-bottom: 0;
  }
}

.mr-response-form {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ece9f5;

  .mr-small {
    margin-bottom: 12px;
  }

  :deep(.textarea-component .label) {
    margin-left: 20px;
    font-size: 16px;
    font-weight: 700;
    color: #484848;
  }
}

.mr-response {
  margin-top: 16px;
  padding: 12px 16px;
  border-left: 3px solid #1e6b3a;
  border-radius: 6px;
  background-color: #f3faf5;

  .mr-small {
    margin-top: 6px;
  }
}

.mr-accepted {
  margin-top: 16px;
}

.mr-reassess {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 0 20px;
  font-size: 14px;
  color: #2b2b3a;
}

.mr-reassess-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #484848;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: $lila-primary;
  }
}

.mr-reassess-days {
  width: 64px;
  padding: 4px 8px;
  border: 1px solid #dcd8e6;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;

  &:disabled {
    background-color: #f4f4f7;
    color: #9a9aa8;
  }
}

.mr-reassess-hint {
  margin: 6px 0 0 20px;
}

.mr-reassess-note {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e6b3a;
}

.mr-response-title {
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #1e6b3a;
}
</style>
